import React, { useState, useEffect } from "react";
// Firebase operations extracted to src/hooks/useVendors
import { Plus, Search, Edit, UserPlus, Loader2 } from "lucide-react";
import { useVendors } from "../hooks/useVendors";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import VendorModal from "../components/modals/VendorModal";

export default function Vendors() {
  
  const {
    vendors,
    categories,
    availableTags,
    availableBrandTags,
    loading,
    error,
    setError,
    addVendor,
    updateVendor
  } = useVendors();

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentVendorId, setCurrentVendorId] = useState(null);

  const [paySchedules, setPaySchedules] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "pay_schedules"), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setPaySchedules(list);
    }, (err) => {
      console.error("Error loading pay schedules in Vendors:", err);
    });
    return () => unsubscribe();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    category: "Logistics",
    tags: [],
    brandTags: [],
    status: "Active",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    paySchedule: "",
    pocs: [{ name: "", title: "", email: "", phone: "" }],
    taxExempt: "no",
    accountUser: "",
    accountPassword: "",
    url: "",
    notes: "",
    lastPurchasedDate: ""
  });

  const [tagInput, setTagInput] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  function handleOpenAddModal() {
    setIsEditMode(false);
    setError("");
    setFormData({
      name: "",
      category: categories[0] || "Logistics",
      tags: [],
      brandTags: [],
      status: "Active",
      address: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      paySchedule: "",
      pocs: [{ name: "", title: "", email: "", phone: "" }],
      taxExempt: "no",
      taxExemptExpiration: "",
      blacklist: false,
      blacklistReason: "",
      dueDiligenceDone: false,
      accountUser: "",
      accountPassword: "",
      url: "",
      notes: "",
      lastPurchasedDate: "",
      dateLastVerified: ""
    });
    setTagInput("");
    setModalOpen(true);
  }

  function handleOpenEditModal(vendor) {
    setIsEditMode(true);
    setCurrentVendorId(vendor.id);
    setError("");
    
    const normalizedPocs = vendor.pocs && vendor.pocs.length > 0 ? vendor.pocs : [
      {
        name: vendor.contactName || "",
        title: vendor.pocTitle || "",
        email: vendor.email || "",
        phone: vendor.phone || ""
      }
    ];

    setFormData({
      name: vendor.name,
      category: vendor.category,
      tags: vendor.tags || [],
      brandTags: vendor.brandTags || [],
      status: vendor.status,
      address: vendor.address || "",
      address2: vendor.address2 || "",
      city: vendor.city || "",
      state: vendor.state || "",
      zip: vendor.zip || "",
      paySchedule: vendor.paySchedule || "",
      pocs: normalizedPocs,
      taxExempt: vendor.taxExempt || "no",
      taxExemptExpiration: vendor.taxExemptExpiration || "",
      blacklist: vendor.blacklist || false,
      blacklistReason: vendor.blacklistReason || "",
      dueDiligenceDone: vendor.dueDiligenceDone || false,
      accountUser: vendor.accountUser || "",
      accountPassword: vendor.accountPassword || "",
      url: vendor.url || "",
      notes: vendor.notes || "",
      lastPurchasedDate: vendor.lastPurchasedDate || "",
      dateLastVerified: vendor.dateLastVerified || ""
    });
    setTagInput("");
    setModalOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const updatedFormData = {
      ...formData,
      dateLastVerified: new Date().toISOString().split("T")[0]
    };
    try {
      if (isEditMode) {
        await updateVendor(currentVendorId, updatedFormData);
      } else {
        await addVendor(updatedFormData);
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Error saving vendor:", err);
      // setError is handled inside useVendors or we can just rely on the hook's error state, 
      // but we might want local error display. We'll use the hook's setError.
    }
  }

  const filteredVendors = vendors.filter(vendor => {
    const pocs = vendor.pocs && vendor.pocs.length > 0 ? vendor.pocs : [
      { name: vendor.contactName || "", email: vendor.email || "", phone: vendor.phone || "", title: vendor.pocTitle || "" }
    ];
    
    const searchLower = searchTerm.toLowerCase();
    
    const matchesName = vendor.name.toLowerCase().includes(searchLower);
    const matchesAddress = [vendor.address, vendor.city, vendor.state, vendor.zip]
      .filter(Boolean)
      .some(field => field.toLowerCase().includes(searchLower));
      
    const matchesPocs = pocs.some(poc => 
      (poc.name && poc.name.toLowerCase().includes(searchLower)) ||
      (poc.title && poc.title.toLowerCase().includes(searchLower)) ||
      (poc.email && poc.email.toLowerCase().includes(searchLower)) ||
      (poc.phone && poc.phone.toLowerCase().includes(searchLower))
    );
    
    const matchesTags = vendor.tags && vendor.tags.some(t => t.toLowerCase().includes(searchLower));
    const matchesBrandTags = vendor.brandTags && vendor.brandTags.some(t => t.toLowerCase().includes(searchLower));
    
    const matchesSearch = matchesName || matchesAddress || matchesPocs || matchesTags || matchesBrandTags;
    const matchesCategory = categoryFilter === "All" || vendor.category === categoryFilter;
    
    // Tag search override: if searching by tag, it pulls inactive or blacklist status (i.e. ignore status filter)
    const isTagSearchMatch = searchTerm && (matchesTags || matchesBrandTags);
    const matchesStatus = isTagSearchMatch || statusFilter === "All" || vendor.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Vendors Management</h1>
          <p>Register, evaluate, and track supply chain partner details.</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          <Plus size={18} />
          Register Vendor
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="table-actions-container">
        <div className="search-box-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search vendors by name, contact, email or tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-select-group" style={{ display: "flex", gap: "0.75rem" }}>
          <select
            className="form-input filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ minWidth: "150px" }}
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
            <option value="Blacklist">Blacklist Only</option>
          </select>

          <select
            className="form-input filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ minWidth: "150px" }}
          >
            <option value="All">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Vendor List Table */}
      {loading && vendors.length === 0 ? (
        <div className="loading-container">
          <Loader2 className="spinner-icon" size={36} />
          <span>Loading vendors database...</span>
        </div>
      ) : filteredVendors.length === 0 ? (
        <div className="empty-state-card">
          <UserPlus size={48} className="empty-state-icon" />
          <h3>No Vendors Found</h3>
          <p>Try refining your search terms or register a new vendor partner.</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Vendor Name</th>
                <th>Category</th>
                <th>Contact Person</th>
                <th>Contact Details</th>
                <th>Tags & Brands</th>
                <th>Status</th>
                <th>Last Verified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor) => {
                const primaryPoc = (vendor.pocs && vendor.pocs.length > 0) ? vendor.pocs[0] : {
                  name: vendor.contactName || "",
                  title: vendor.pocTitle || "",
                  email: vendor.email || "",
                  phone: vendor.phone || ""
                };
                const extraPocCount = vendor.pocs ? vendor.pocs.length - 1 : 0;

                return (
                  <tr key={vendor.id}>
                    <td>
                      <div className="vendor-primary-name" style={{ color: "var(--text-primary)", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        {vendor.name}
                        {vendor.dueDiligenceDone && (
                          <span 
                            className="sku-badge" 
                            style={{ 
                              fontSize: "0.65rem", 
                              backgroundColor: "rgba(76, 175, 80, 0.15)", 
                              color: "var(--success)",
                              border: "1px solid rgba(76, 175, 80, 0.3)"
                            }}
                            title="Due Diligence Completed"
                          >
                            Due Diligence Done
                          </span>
                        )}
                      </div>
                      {(vendor.city || vendor.state) && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                          {[vendor.address, vendor.city, vendor.state].filter(Boolean).join(", ")}
                        </div>
                      )}
                      {vendor.lastPurchasedDate && (
                        <div style={{ fontSize: "0.75rem", color: "var(--primary)", marginTop: "0.25rem", fontWeight: "500" }}>
                          Last Purchased: {vendor.lastPurchasedDate}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-info">{vendor.category}</span>
                      {vendor.paySchedule && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                          Pay: {vendor.paySchedule}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="poc-name-text" style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                        {primaryPoc.name || <span className="text-secondary" style={{ fontStyle: "italic", fontWeight: "normal" }}>No Contact</span>}
                      </div>
                      {primaryPoc.title && <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{primaryPoc.title}</div>}
                      {extraPocCount > 0 && (
                        <span className="sku-badge" style={{ fontSize: "0.65rem", background: "rgba(255,255,255,0.05)", color: "var(--text-secondary)", marginTop: "0.2rem", display: "inline-block" }}>
                          +{extraPocCount} more POC{extraPocCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="vendor-contact-details">
                        <span style={{ color: "var(--text-primary)", display: "block" }}>{primaryPoc.email}</span>
                        <span className="phone-sub" style={{ color: "var(--text-secondary)", display: "block" }}>{primaryPoc.phone}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", maxWidth: "240px" }}>
                        {vendor.tags && vendor.tags.map(tag => (
                          <span key={tag} className="sku-badge" style={{ fontSize: "0.75rem", background: "rgba(33, 150, 243, 0.15)", color: "var(--primary)", border: "1px solid rgba(33, 150, 243, 0.3)" }}>
                            {tag}
                          </span>
                        ))}
                        {vendor.brandTags && vendor.brandTags.map(brand => (
                          <span key={brand} className="sku-badge" style={{ fontSize: "0.75rem", background: "rgba(16, 185, 129, 0.15)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
                            {brand}
                          </span>
                        ))}
                        {(!vendor.tags || vendor.tags.length === 0) && (!vendor.brandTags || vendor.brandTags.length === 0) && (
                          <span className="text-secondary" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>No tags</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${
                        vendor.status === "Active" 
                          ? "badge-success" 
                          : vendor.status === "Blacklist" 
                          ? "badge-blacklist" 
                          : "badge-danger"
                      }`}>
                        {vendor.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                        {vendor.dateLastVerified || "—"}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-icon"
                        onClick={() => handleOpenEditModal(vendor)}
                        title="Edit Vendor"
                      >
                        <Edit size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Register/Edit Vendor Modal */}
      <VendorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        isEditMode={isEditMode}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        error={error}
        categories={categories}
        paySchedules={paySchedules}
        tagInput={tagInput}
        setTagInput={setTagInput}
        showTagDropdown={showTagDropdown}
        setShowTagDropdown={setShowTagDropdown}
        availableTags={availableTags}
        availableBrandTags={availableBrandTags}
        loading={loading}
      />

      <style>{`
        .badge-blacklist {
          background-color: rgba(231, 76, 60, 0.12);
          color: #ff4a5a;
          border: 1px solid rgba(231, 76, 60, 0.35);
        }

        .table-actions-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .search-box-container {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: var(--text-muted);
        }

        .search-input {
          padding-left: 2.75rem;
        }

        .filter-select {
          min-width: 180px;
        }

        .vendor-primary-name {
          font-weight: 600;
          font-family: var(--font-heading);
          color: var(--text-primary);
        }

        .vendor-contact-details {
          display: flex;
          flex-direction: column;
        }

        .phone-sub {
          color: var(--text-secondary);
          font-size: 0.8rem;
          margin-top: 0.1rem;
        }



        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem 0;
          gap: 1rem;
          color: var(--text-secondary);
        }

        .spinner-icon {
          animation: spin 1s linear infinite;
        }

        .empty-state-card {
          text-align: center;
          padding: 4rem 2rem;
          background-color: var(--bg-card);
          border: 1px dashed var(--border-color);
          border-radius: var(--radius-md);
        }

        .empty-state-icon {
          color: var(--text-muted);
          margin-bottom: 1rem;
        }

        .empty-state-card h3 {
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .empty-state-card p {
          color: var(--text-secondary);
          max-width: 320px;
          margin: 0 auto;
          font-size: 0.9rem;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .table-actions-container {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }
        }

        .tag-suggestions-dropdown div:hover {
          background-color: var(--primary-hover) !important;
          color: #ffffff !important;
        }
      `}</style>
    </div>
  );
}
